import { useEffect, useMemo, useState } from 'react'
import { KILO } from '../../constants/KILO_THEME'
import { supabase } from '../../lib/supabaseClient'
import { SkeletonImage } from '../ui/Skeleton'

type MealImageProps = {
  mealName: string
  mealDescription: string
  imageUrl?: string | null
  ingredients?: { name: string; amount?: string; quantity?: string }[]
  className?: string
}

type ImageResult = {
  image_url: string
  source: 'gemini_imagen' | 'placeholder'
  from_cache: boolean
}

function isValidImageUrl(url: string | null | undefined): url is string {
  if (!url?.trim()) return false
  if (url.includes('_placeholder.jpg')) return false
  if (url.includes('placehold.co')) return false
  return true
}

const MealImage = ({
  mealName,
  mealDescription,
  imageUrl,
  ingredients = [],
  className = '',
}: MealImageProps) => {
  const resolvedInitialUrl = isValidImageUrl(imageUrl) ? imageUrl : null
  const [result, setResult] = useState<ImageResult | null>(
    resolvedInitialUrl
      ? { image_url: resolvedInitialUrl, source: 'gemini_imagen', from_cache: true }
      : null,
  )
  const [loading, setLoading] = useState(!resolvedInitialUrl)
  const [error, setError] = useState(false)
  const ingredientsKey = useMemo(
    () =>
      JSON.stringify(
        ingredients.map((i) => ({ name: i.name, quantity: i.quantity ?? i.amount ?? '' })),
      ),
    [ingredients],
  )

  useEffect(() => {
    if (isValidImageUrl(imageUrl)) {
      setResult({ image_url: imageUrl, source: 'gemini_imagen', from_cache: true })
      setLoading(false)
      setError(false)
      return
    }

    if (!mealName || !mealDescription) {
      setLoading(false)
      setError(true)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(false)

    const payloadIngredients = ingredients.map((i) => ({
      name: i.name,
      quantity: i.quantity ?? i.amount ?? '',
    }))

    supabase.functions
      .invoke('get-meal-image', {
        body: {
          name: mealName,
          description: mealDescription,
          ingredients: payloadIngredients,
        },
      })
      .then(({ data, error: invokeError }) => {
        if (cancelled) return
        if (invokeError) throw invokeError
        if (data?.error) throw new Error(String(data.error))
        if (!isValidImageUrl(data?.image_url)) throw new Error('No image generated')
        setResult(data as ImageResult)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('MealImage fetch error:', err)
        setError(true)
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [mealName, mealDescription, imageUrl, ingredientsKey])

  if (loading) {
    return <SkeletonImage className={className} aria-label="generating preview" />
  }

  if (error || !result) {
    return (
      <div className={`relative flex items-center justify-center rounded-xl bg-kilo-nested ${className}`}>
        <div className={`flex flex-col items-center gap-2 ${KILO.text.soft}`}>
          <span className="text-2xl">🍽️</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">visual asset pending</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative overflow-hidden rounded-xl ${className}`}>
      <img
        src={result.image_url}
        alt={mealName}
        className="h-full w-full object-cover"
        loading="lazy"
        onError={() => setError(true)}
      />
    </div>
  )
}

export default MealImage
